// Team Reports — flexible analytics view. Three filter dimensions:
//   Who:  All / specific employees / by role
//   When: Today / This Week / This Month / Custom date range
//   View: Cards (KPI rollup) / Table (flat per-day per-person) / Calendar (one person × full month grid)
//
// Admin-only mgmt remarks editing, Excel export of the current filter set.

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  DatePicker, Select, Tag, Tooltip, Input, Button, Table, message, Tabs,
  Empty, Spin, Modal,
} from "antd";
import { DownloadOutlined, FilterOutlined, FileExcelOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  fetchManualReportRoleConfig,
  fetchTeamMembers,
  fetchTeamReports,
  fetchPeriodSummary,
  exportReportsExcel,
  updateMgmtRemarks,
} from "../../../Slices/manualReportsSlice";
import { formatIso, formatDmy, parseCounts, computeDayGrandTotal, computeKPIs } from "./manualReportUtils";

const { RangePicker } = DatePicker;

const COLORS = {
  bg: "#f9fafb",
  surface: "#fff",
  border: "#e5e7eb",
  text: "#111827",
  textMuted: "#6b7280",
  surfaceAlt: "#fafafa",
};

const PRESETS = [
  { key: "today", label: "Today", range: () => [dayjs().startOf("day"), dayjs().endOf("day")] },
  { key: "thisWeek", label: "This Week", range: () => [dayjs().startOf("isoWeek"), dayjs().endOf("isoWeek")] },
  { key: "thisMonth", label: "This Month", range: () => [dayjs().startOf("month"), dayjs().endOf("month")] },
  { key: "lastMonth", label: "Last Month", range: () => [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")] },
  { key: "ytd", label: "Year to date", range: () => [dayjs().startOf("year"), dayjs().endOf("day")] },
];

// ─── KPI Card ───────────────────────────────────────────────────────────
const KPICard = ({ label, value, color, sub }) => (
  <div style={{
    flex: 1,
    minWidth: 150,
    background: COLORS.surface,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: "14px 18px",
    position: "relative",
    overflow: "hidden",
  }}>
    <div style={{
      position: "absolute", top: -10, right: -10, width: 60, height: 60,
      background: `radial-gradient(circle, ${color}15 0%, transparent 70%)`, borderRadius: "50%",
    }} />
    <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, letterSpacing: 1.2, textTransform: "uppercase" }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 800, color, marginTop: 4, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
      {(value || 0).toLocaleString()}
    </div>
    {sub && <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 4, fontFamily: "monospace" }}>{sub}</div>}
  </div>
);

const initialsOf = (name) => (name || "").split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

// ─── Cards view ─────────────────────────────────────────────────────────
const CardsView = ({ summary, config }) => {
  if (!summary || !config) return null;
  const kpi = JSON.parse(summary.teamKpiJson || "{}");
  const teamTotals = JSON.parse(summary.teamTotalsJson || "{}");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {config.kpis?.map(k => (
          <KPICard key={k.key} label={k.label} value={kpi[k.key]} color={k.color} />
        ))}
        <KPICard label="Total" value={kpi.total} color="#a78bfa" sub={`${summary.entryCount} entries · ${summary.memberCount} people`} />
      </div>

      {/* Per-member rollup */}
      <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "14px 18px" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, letterSpacing: 1.4, textTransform: "uppercase", marginBottom: 12 }}>Per Member</div>
        {(summary.perMember || []).length === 0 ? (
          <Empty description="No entries in this period" />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(summary.perMember || []).map(m => {
              const mKpi = JSON.parse(m.kpiJson || "{}");
              return (
                <div key={m.employeeId} style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(180px, 1.5fr) repeat(5, 90px) 90px",
                  alignItems: "center", gap: 10,
                  padding: "10px 12px",
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 10,
                  background: COLORS.surfaceAlt,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 8,
                      background: "#e0e7ff", color: "#4338ca",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11, fontWeight: 800, fontFamily: "monospace",
                    }}>{initialsOf(m.employeeName)}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.employeeName}</div>
                      <div style={{ fontSize: 10, color: COLORS.textMuted, fontWeight: 500 }}>{m.entryCount} entries</div>
                    </div>
                  </div>
                  {config.kpis?.map(k => (
                    <div key={k.key} style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: mKpi[k.key] ? k.color : "#d1d5db", fontVariantNumeric: "tabular-nums" }}>
                        {mKpi[k.key] || 0}
                      </div>
                      <div style={{ fontSize: 9, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>{k.label}</div>
                    </div>
                  ))}
                  <div style={{ textAlign: "center", padding: "4px 10px", background: "#f3f4f6", borderRadius: 6 }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#4338ca", fontVariantNumeric: "tabular-nums" }}>{mKpi.total || 0}</div>
                    <div style={{ fontSize: 9, color: COLORS.textMuted }}>TOTAL</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Table view ─────────────────────────────────────────────────────────
const TableView = ({ entries, config, onEditMgmt, isAdmin }) => {
  const data = useMemo(() => entries.map(e => {
    const counts = parseCounts(e);
    const dayTotal = computeDayGrandTotal(config, counts);
    const kpi = computeKPIs(config, counts);
    return {
      key: e.id,
      employee: e.employeeName,
      date: e.date ? dayjs(e.date).format("DD MMM YYYY") : "",
      day: e.day,
      week: `W${e.week}`,
      login: e.attendance?.loginTime || "",
      logout: e.attendance?.logoutTime || "",
      breakIn: e.attendance?.breakIn || "",
      breakOut: e.attendance?.breakOut || "",
      duration: e.attendance?.duration || "",
      permissions: e.attendance?.permissions || "",
      leads: kpi.leads,
      linkedin: kpi.linkedin,
      emails: kpi.emails,
      calls: kpi.calls,
      data: kpi.data,
      total: dayTotal,
      leadIds: e.leadIds,
      remarks: e.remarks,
      meetings: e.meetings,
      complaints: e.complaints,
      mgmtRemarks: e.mgmtRemarks,
      updatedAt: e.updatedAt,
      counts,    // for per-field breakdown in expanded row
      raw: e,
    };
  }), [entries, config]);

  // Category accent palette — matches the form's color coding.
  const catAccent = {
    leads:    '#059669',
    linkedin: '#2563eb',
    emails:   '#d97706',
    calls:    '#db2777',
    data:     '#7c3aed',
    other:    '#64748b',
  };

  // Slimmer main row — at-a-glance numbers only. Text fields live in the
  // expandable detail row below.
  const cols = [
    { title: "Employee", dataIndex: "employee", fixed: "left", width: 170, sorter: (a, b) => a.employee.localeCompare(b.employee) },
    { title: "Date", dataIndex: "date", width: 110, sorter: (a, b) => new Date(a.raw.date) - new Date(b.raw.date) },
    { title: "Day", dataIndex: "day", width: 90 },
    { title: "Wk", dataIndex: "week", width: 50, align: "center" },
    { title: "Login → Logout", width: 160, render: (_, r) => r.login || r.logout ? <span style={{ fontSize: 11 }}>{r.login || "—"} → {r.logout || "—"}</span> : <em style={{ color: COLORS.textMuted }}>—</em> },
    { title: "Permissions", dataIndex: "permissions", width: 110, ellipsis: true, render: (v) => v ? <Tooltip title={v}><span style={{ fontSize: 11 }}>{v}</span></Tooltip> : <em style={{ color: COLORS.textMuted }}>—</em> },
    { title: "Leads",    dataIndex: "leads",    width: 70, align: "right", sorter: (a, b) => a.leads - b.leads,       render: (v) => <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: v ? "#059669" : "#d1d5db" }}>{v || 0}</span> },
    { title: "LinkedIn", dataIndex: "linkedin", width: 80, align: "right", sorter: (a, b) => a.linkedin - b.linkedin, render: (v) => <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: v ? "#2563eb" : "#d1d5db" }}>{v || 0}</span> },
    { title: "Emails",   dataIndex: "emails",   width: 70, align: "right", sorter: (a, b) => a.emails - b.emails,     render: (v) => <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: v ? "#d97706" : "#d1d5db" }}>{v || 0}</span> },
    { title: "Calls",    dataIndex: "calls",    width: 65, align: "right", sorter: (a, b) => a.calls - b.calls,       render: (v) => <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: v ? "#db2777" : "#d1d5db" }}>{v || 0}</span> },
    { title: "Data",     dataIndex: "data",     width: 65, align: "right", sorter: (a, b) => a.data - b.data,         render: (v) => <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: v ? "#7c3aed" : "#d1d5db" }}>{v || 0}</span> },
    { title: "Total",    dataIndex: "total",    width: 75, align: "right", fixed: "right", sorter: (a, b) => a.total - b.total, render: (v) => <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 800, color: v ? "#0f172a" : "#94a3b8" }}>{v || 0}</span> },
  ];

  // Expanded row — proper card-per-section layout.
  // Each section is a bordered card, each field inside is a mini tile.
  const expandedRowRender = (r) => {

    // Section wrapper — colored top stripe + title + body
    const SectionCard = ({ title, accent, children }) => (
      <div style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        overflow: 'hidden',
        position: 'relative',
      }}>
        <div style={{
          height: 3, background: accent, opacity: 0.85,
        }} />
        <div style={{ padding: '12px 14px' }}>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: 0.6,
            color: accent, textTransform: 'uppercase',
            marginBottom: 10,
          }}>{title}</div>
          {children}
        </div>
      </div>
    );

    // Numeric tile — for activity counts
    const NumTile = ({ label, value, accent }) => (
      <div style={{
        flex: '1 1 140px', minWidth: 130,
        background: value ? `${accent}10` : '#f8fafc',
        border: `1px solid ${value ? accent + '30' : '#e2e8f0'}`,
        borderRadius: 6,
        padding: '8px 10px',
        display: 'flex', flexDirection: 'column', gap: 4,
      }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: '#64748b', letterSpacing: 0.3, lineHeight: 1.3 }}>{label}</div>
        <div style={{
          fontSize: 18, fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
          color: value ? accent : '#cbd5e1',
          lineHeight: 1,
        }}>{value || 0}</div>
      </div>
    );

    // Text tile — for attendance and notes
    const TextTile = ({ label, value, mono, minWidth = 160, flex = '1 1 160px' }) => (
      <div style={{
        flex, minWidth,
        background: '#fafbfc',
        border: '1px solid #e2e8f0',
        borderRadius: 6,
        padding: '8px 10px',
      }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: '#64748b', letterSpacing: 0.3, marginBottom: 4 }}>{label}</div>
        <div style={{
          fontSize: 12,
          color: value ? '#1f2937' : '#94a3b8',
          fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          lineHeight: 1.5,
          minHeight: 18,
        }}>{value || <em>—</em>}</div>
      </div>
    );

    return (
      <div style={{
        padding: '14px 18px',
        background: '#f1f5f9',
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        {/* ATTENDANCE */}
        <SectionCard title="Attendance" accent="#2563eb">
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <TextTile label="Login Time"  value={r.login} />
            <TextTile label="Logout Time" value={r.logout} />
            <TextTile label="Break In"    value={r.breakIn} />
            <TextTile label="Break Out"   value={r.breakOut} />
            <TextTile label="Duration"    value={r.duration} />
            <TextTile label="Permissions" value={r.permissions} />
          </div>
        </SectionCard>

        {/* ACTIVITY COUNTS — one card per category */}
        {(config?.categories || []).map(cat => {
          const accent = catAccent[cat.key] || '#64748b';
          return (
            <SectionCard key={cat.key} title={cat.label} accent={accent}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {cat.fields.map(f => (
                  <NumTile
                    key={f.key}
                    label={f.label}
                    value={r.counts?.[f.key] || 0}
                    accent={accent}
                  />
                ))}
              </div>
            </SectionCard>
          );
        })}

        {/* NOTES */}
        <SectionCard title="Notes" accent="#7c3aed">
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <TextTile label="Lead IDs"            value={r.leadIds}    mono flex="2 1 280px" minWidth={260} />
            <TextTile label="Remarks"             value={r.remarks}         flex="1 1 220px" minWidth={200} />
            <TextTile label="Meetings with Team"  value={r.meetings}        flex="1 1 220px" minWidth={200} />
            <TextTile label="Complaints / Issues" value={r.complaints}      flex="1 1 220px" minWidth={200} />
          </div>
        </SectionCard>

        {/* MANAGEMENT REMARKS + META */}
        <SectionCard title="Management" accent="#0f766e">
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{
              flex: '2 1 320px', minWidth: 280,
              background: '#fafbfc',
              border: '1px solid #e2e8f0',
              borderRadius: 6,
              padding: '8px 10px',
            }}>
              <div style={{
                fontSize: 10, fontWeight: 600, color: '#64748b', letterSpacing: 0.3,
                marginBottom: 4,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span>Management Remarks</span>
                {isAdmin && (
                  <Button size="small" type="link" onClick={() => onEditMgmt(r.raw)}
                    style={{ padding: 0, height: 'auto', fontSize: 11 }}>
                    Edit
                  </Button>
                )}
              </div>
              <div style={{
                fontSize: 12,
                color: r.mgmtRemarks ? '#1f2937' : '#94a3b8',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                lineHeight: 1.5,
                minHeight: 18,
              }}>{r.mgmtRemarks || <em>—</em>}</div>
            </div>
            <TextTile
              label="Last Updated"
              value={r.updatedAt ? dayjs(r.updatedAt).format('DD MMM YYYY, h:mm A') : ''}
              flex="1 1 200px"
              minWidth={180}
            />
          </div>
        </SectionCard>
      </div>
    );
  };

  return (
    <Table
      columns={cols}
      dataSource={data}
      size="small"
      bordered
      scroll={{ x: 1100 }}
      pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} entries` }}
      expandable={{
        expandedRowRender,
        expandRowByClick: false,
      }}
    />
  );
};

// ─── Calendar view (one person, full month grid) ────────────────────────
const CalendarView = ({ entries, config, selectedEmployeeId, employees }) => {
  // If no one is selected, prompt the user. Otherwise build a month grid for that person.
  if (!selectedEmployeeId) {
    return (
      <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 40, textAlign: "center", color: COLORS.textMuted }}>
        Select <b>one</b> employee in the filter above to see their full-month calendar.
      </div>
    );
  }
  const employee = employees.find(e => e.employeeId === selectedEmployeeId);
  const personEntries = entries.filter(e => e.employeeId === selectedEmployeeId);
  if (personEntries.length === 0) {
    return <Empty description={`No entries for ${employee?.name || "this person"} in the selected range`} />;
  }

  // Group by (year-month), then render one month grid each.
  const byMonth = new Map();
  for (const e of personEntries) {
    const k = `${e.year}-${e.monthIndex}`;
    if (!byMonth.has(k)) byMonth.set(k, { year: e.year, monthIndex: e.monthIndex, month: e.month, entries: [] });
    byMonth.get(k).entries.push(e);
  }

  const monthGrid = (year, monthIndex, monthEntries) => {
    const firstDayOfMonth = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0).getDate();
    const jsDow = firstDayOfMonth.getDay(); // 0=Sun
    const leading = jsDow === 0 ? 6 : jsDow - 1; // make Mon=0
    const cells = [];
    for (let i = 0; i < leading; i++) cells.push({ blank: true });
    const byDay = new Map();
    for (const e of monthEntries) byDay.set(new Date(e.date).getUTCDate(), e);
    for (let d = 1; d <= lastDay; d++) {
      const entry = byDay.get(d);
      const counts = parseCounts(entry);
      const total = computeDayGrandTotal(config, counts);
      cells.push({ d, entry, total, hasData: !!entry });
    }
    return cells;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {[...byMonth.values()].map(({ year, monthIndex, month, entries: ents }) => {
        const cells = monthGrid(year, monthIndex, ents);
        return (
          <div key={`${year}-${monthIndex}`} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "16px 18px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{month} {year}</div>
              <div style={{ fontSize: 11, color: COLORS.textMuted }}>{ents.length} days logged · {ents.reduce((s, e) => s + computeDayGrandTotal(config, parseCounts(e)), 0)} total activities</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(d => (
                <div key={d} style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, textAlign: "center", paddingBottom: 4 }}>{d}</div>
              ))}
              {cells.map((c, i) => c.blank ? (
                <div key={i} style={{ aspectRatio: "1 / 1" }} />
              ) : (
                <Tooltip key={i} title={c.entry ? (
                  <div style={{ fontSize: 11 }}>
                    <div><b>Login:</b> {c.entry.attendance?.loginTime || "—"} → {c.entry.attendance?.logoutTime || "—"}</div>
                    <div><b>Total activities:</b> {c.total}</div>
                    {c.entry.remarks && <div style={{ marginTop: 4 }}>{c.entry.remarks}</div>}
                  </div>
                ) : "No entry"}>
                  <div style={{
                    aspectRatio: "1 / 1",
                    borderRadius: 8,
                    border: `1px solid ${c.hasData ? "#16a34a" : COLORS.border}`,
                    background: c.hasData ? `rgba(22,163,74,${Math.min(0.05 + (c.total / 100) * 0.4, 0.5)})` : COLORS.surfaceAlt,
                    display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center",
                    cursor: "pointer", transition: "transform 0.15s",
                  }}>
                    <div style={{ fontSize: 11, color: COLORS.textMuted }}>{c.d}</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: c.hasData ? "#15803d" : "#d1d5db", fontVariantNumeric: "tabular-nums" }}>{c.hasData ? c.total : "—"}</div>
                  </div>
                </Tooltip>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Main page ──────────────────────────────────────────────────────────
export default function TeamReportsPage() {
  const dispatch = useDispatch();
  const config = useSelector((s) => s.manualReports.config);
  const teamMembers = useSelector((s) => s.manualReports.teamMembers);
  const entries = useSelector((s) => s.manualReports.teamReports);
  const summary = useSelector((s) => s.manualReports.periodSummary);
  const loading = useSelector((s) => s.manualReports.loading);
  const loginUser = useSelector((s) => s.loginUserInfo?.loginUserDetails);
  const isAdmin = loginUser?.role === "Admin" || loginUser?.role === "Super Admin";

  const [range, setRange] = useState(PRESETS[2].range()); // This Month default
  const [presetKey, setPresetKey] = useState("thisMonth");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [view, setView] = useState("cards");
  const [exporting, setExporting] = useState(false);
  const [mgmtEdit, setMgmtEdit] = useState({ open: false, entry: null, text: "" });

  useEffect(() => {
    if (!config) dispatch(fetchManualReportRoleConfig());
    if (!teamMembers.length) dispatch(fetchTeamMembers());
  }, [dispatch, config, teamMembers.length]);

  const fromIso = range?.[0] ? range[0].format("YYYY-MM-DD") : null;
  const toIso = range?.[1] ? range[1].format("YYYY-MM-DD") : null;

  useEffect(() => {
    if (!fromIso || !toIso) return;
    const args = {
      fromDate: fromIso,
      toDate: toIso,
      employeeIds: selectedEmployees.length ? selectedEmployees : null,
    };
    dispatch(fetchTeamReports(args));
    dispatch(fetchPeriodSummary(args));
  }, [dispatch, fromIso, toIso, selectedEmployees.join(",")]);

  const handleExport = async () => {
    if (!fromIso || !toIso) return;
    setExporting(true);
    try {
      const result = await dispatch(exportReportsExcel({
        fromDate: fromIso, toDate: toIso,
        employeeIds: selectedEmployees.length ? selectedEmployees : null,
      })).unwrap();
      if (result?.base64) {
        const bytes = Uint8Array.from(atob(result.base64), c => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = result.filename || "manual_reports.xlsx";
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        message.success("Exported");
      }
    } catch (err) {
      message.error(err || "Export failed");
    } finally { setExporting(false); }
  };

  const saveMgmtRemarks = async () => {
    if (!mgmtEdit.entry) return;
    try {
      await dispatch(updateMgmtRemarks({ entryId: mgmtEdit.entry.id, mgmtRemarks: mgmtEdit.text })).unwrap();
      message.success("Saved");
      setMgmtEdit({ open: false, entry: null, text: "" });
    } catch (err) { message.error(err || "Failed"); }
  };

  return (
    <div style={{ padding: "24px 28px 60px", background: COLORS.bg, minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: COLORS.text }}>Team Reports</h1>
        <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>
          {summary
            ? `${summary.entryCount} entries from ${summary.memberCount} team member${summary.memberCount === 1 ? "" : "s"}`
            : "Loading…"}
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "14px 18px", marginBottom: 16, display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6 }}>Date Range</div>
          <RangePicker
            value={range}
            onChange={(r) => { setRange(r); setPresetKey(null); }}
            allowClear={false}
            style={{ width: 280 }}
          />
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6 }}>Quick Picks</div>
          <div style={{ display: "flex", gap: 4 }}>
            {PRESETS.map(p => (
              <Button
                key={p.key}
                size="small"
                type={presetKey === p.key ? "primary" : "default"}
                onClick={() => { setRange(p.range()); setPresetKey(p.key); }}
              >{p.label}</Button>
            ))}
          </div>
        </div>
        <div style={{ minWidth: 220 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6 }}>Employees</div>
          <Select
            mode="multiple"
            placeholder="All"
            value={selectedEmployees}
            onChange={setSelectedEmployees}
            style={{ minWidth: 220 }}
            maxTagCount="responsive"
            options={teamMembers.map(m => ({ label: m.name, value: m.employeeId }))}
            allowClear
          />
        </div>
        <div style={{ flex: 1 }} />
        {isAdmin && (
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            loading={exporting}
            onClick={handleExport}
            disabled={!fromIso || !toIso || (entries?.length === 0)}
          >
            Export Excel
          </Button>
        )}
      </div>

      {/* View tabs */}
      <Tabs
        activeKey={view}
        onChange={setView}
        items={[
          { key: "cards", label: "Cards (KPI rollup)" },
          { key: "table", label: "Table (all entries)" },
          { key: "calendar", label: "Calendar (one person × month)" },
        ]}
      />

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}><Spin /></div>
      ) : view === "cards" ? (
        <CardsView summary={summary} config={config} />
      ) : view === "table" ? (
        <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 8 }}>
          <TableView entries={entries} config={config} isAdmin={isAdmin} onEditMgmt={(e) => setMgmtEdit({ open: true, entry: e, text: e.mgmtRemarks || "" })} />
        </div>
      ) : (
        <CalendarView
          entries={entries}
          config={config}
          selectedEmployeeId={selectedEmployees.length === 1 ? selectedEmployees[0] : null}
          employees={teamMembers}
        />
      )}

      {/* Mgmt Remarks Edit Modal */}
      <Modal
        title={`Management Remarks · ${mgmtEdit.entry?.employeeName || ""} · ${mgmtEdit.entry?.day || ""}`}
        open={mgmtEdit.open}
        onCancel={() => setMgmtEdit({ open: false, entry: null, text: "" })}
        onOk={saveMgmtRemarks}
        okText="Save"
      >
        <Input.TextArea
          value={mgmtEdit.text}
          onChange={(e) => setMgmtEdit({ ...mgmtEdit, text: e.target.value })}
          rows={4}
          placeholder="Remarks visible to the employee and other admins…"
        />
      </Modal>
    </div>
  );
}
